import i18next from 'i18next'
import type { ReminderPlan } from '@/src/shared/types/reminders'
import {
  createReminderScheduler,
  type ReminderScheduler,
  type ReminderSchedulerDeps,
  type StoredReminderState,
} from '@/src/shared/reminders/reminderScheduler'
import type { ReminderDelivery, ReminderPermission } from './types'

/*
  **Erinnerungen im Browser** (Plan task-reminders, Etappe 4): derselbe Zeitgeber wie am Desktop,
  nur im Tab — er klingelt, solange ein Tab offen ist (Entscheidung 10). Gefeuertes und der Plan
  liegen im `localStorage`: Ein Neuladen klingelt nichts doppelt, und wer den Tab wieder öffnet,
  bekommt Verpasstes der letzten 12 Stunden nach, wie am Desktop.

  **Nur ein Tab stellt zu.** Jeder offene Tab rechnet denselben Plan; ohne Absprache käme jede
  Mitteilung so oft, wie Tabs offen sind. Ein Web Lock bestimmt den einen, der klingelt — schließt
  er, übernimmt der nächste. Die anderen halten nur ihren neuesten Plan für die Übernahme bereit.
*/

const STATE_KEY = 'reminders.state'
const ENABLED_KEY = 'reminders.enabled'
/** Gesetzt, wenn ein Tab beim Abmelden alles vergisst — der zustellende Tab kann ein anderer sein. */
const CLEARED_KEY = 'reminders.cleared'
const LOCK_NAME = 'happytask-reminders'
const ICON = '/icon-192.png'

/** Was der Browser beisteuert — im Test gestellt. */
export interface WebReminderEnv {
  /** `undefined`: Der Browser kann keine Mitteilungen (z. B. Safari auf dem iPhone ohne Home-Bildschirm). */
  Notification: typeof Notification | undefined
  storage: Pick<Storage, 'getItem' | 'setItem'>
  now: () => number
  setTimer: (run: () => void, ms: number) => unknown
  clearTimer: (timer: unknown) => void
  focus: () => void
  /** Ruft `onLeader`, sobald dieser Tab zustellen darf — höchstens ein Tab zur selben Zeit. */
  acquireLeadership: (onLeader: () => void) => void
  /** Der Tab ist wieder sichtbar — nach Schlaf oder gedrosselten Timern die Zeit nachziehen. */
  onVisible: (listener: () => void) => void
  /** Ein ANDERER Tab hat `key` geschrieben (`storage`-Ereignis). */
  onForeignWrite: (key: string, listener: () => void) => void
  text: (key: string) => string
}

function browserEnv(): WebReminderEnv {
  return {
    Notification: typeof Notification === 'undefined' ? undefined : Notification,
    storage: localStorage,
    now: () => Date.now(),
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: timer => window.clearTimeout(timer as number),
    focus: () => window.focus(),
    acquireLeadership: onLeader => {
      if (!navigator.locks) {
        onLeader()
        return
      }
      // Der Lock gilt, solange das Versprechen offen ist — also bis der Tab schließt.
      navigator.locks.request(LOCK_NAME, () => {
        onLeader()
        return new Promise<never>(() => {})
      }).catch(() => onLeader())
    },
    onVisible: listener => {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') listener()
      })
    },
    onForeignWrite: (key, listener) => {
      window.addEventListener('storage', event => {
        if (event.key === key) listener()
      })
    },
    text: key => i18next.t(key),
  }
}

export function createWebReminderDelivery(env: WebReminderEnv = browserEnv()): ReminderDelivery {
  const listeners = new Set<() => void>()
  let scheduler: ReminderScheduler | null = null
  let latestPlan: ReminderPlan | null = null
  let campaigning = false

  const read = (key: string): string | null => {
    try {
      return env.storage.getItem(key)
    } catch {
      return null
    }
  }
  const write = (key: string, value: string) => {
    try {
      env.storage.setItem(key, value)
    } catch {
      // Privates Fenster oder voller Speicher: Dann klingelt es nach einem Neuladen eben erneut.
    }
  }

  const permission = (): ReminderPermission =>
    env.Notification ? (env.Notification.permission as ReminderPermission) : 'unsupported'

  const deps: ReminderSchedulerDeps = {
    now: env.now,
    load: () => {
      try {
        return JSON.parse(read(STATE_KEY) ?? 'null') as StoredReminderState | null
      } catch {
        return null
      }
    },
    save: state => write(STATE_KEY, JSON.stringify(state)),
    show: (notification, handlers) => {
      const Native = env.Notification
      if (!Native || Native.permission !== 'granted') return { close: () => {} }
      const native = new Native(notification.title, { body: notification.body, icon: ICON })
      native.onclick = handlers.onClick
      native.onclose = handlers.onClose
      return { close: () => native.close() }
    },
    isEnabled: () => read(ENABLED_KEY) !== 'false',
    // Knöpfe an Mitteilungen gibt es im Browser nur über einen Service Worker.
    withActions: false,
    // Die Texte teilt sich der Browser mit dem Main-Prozess, dessen Platzhalter `{x}` heißen.
    text: (key, params) => Object.entries(params ?? {}).reduce(
      (out, [name, value]) => out.replaceAll(`{${name}}`, String(value)),
      env.text(key),
    ),
    revealMainWindow: env.focus,
    hasMainWindow: () => true,
    notifyRenderers: () => listeners.forEach(listener => listener()),
    setTimer: env.setTimer,
    clearTimer: env.clearTimer,
  }

  function lead() {
    scheduler = createReminderScheduler(deps)
    scheduler.start()
    if (latestPlan) scheduler.setPlan(latestPlan)
    env.onVisible(() => scheduler?.wake())
    // Abgemeldet in einem anderen Tab: Der Plan im Speicher dieses Tabs gehört dem alten Konto.
    env.onForeignWrite(CLEARED_KEY, () => { latestPlan = null; scheduler?.clear() })
  }

  async function requestPermission(): Promise<ReminderPermission> {
    const Native = env.Notification
    if (!Native) return 'unsupported'
    if (Native.permission !== 'default') return permission()
    return (await Native.requestPermission()) as ReminderPermission
  }

  return {
    setPlan(plan) {
      latestPlan = plan
      if (scheduler) {
        scheduler.setPlan(plan)
      } else if (!campaigning) {
        campaigning = true
        env.acquireLeadership(lead)
      }
    },
    takeActions: async () => scheduler?.takeActions() ?? [],
    onActionsReady(listener) {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
    async test() {
      await requestPermission()
      // Auch aus einem Tab, der nicht zustellt: Die Probe gehört dem, der geklickt hat.
      if (scheduler) scheduler.test()
      else deps.show({ title: deps.text('reminders.notification.testTitle'), body: deps.text('reminders.notification.testBody'), actions: [] }, { onClick: env.focus, onAction: () => {}, onClose: () => {} })
    },
    getEnabled: async () => deps.isEnabled(),
    setEnabled: async enabled => write(ENABLED_KEY, String(enabled)),
    showWindow: env.focus,
    getPermission: permission,
    requestPermission,
    async clear() {
      latestPlan = null
      if (scheduler) scheduler.clear()
      else write(STATE_KEY, JSON.stringify({ notices: [], fired: {}, lastCheckedAt: env.now(), actions: [] }))
      write(CLEARED_KEY, String(env.now()))
    },
  }
}
